/**
 * The HTTP client: fetch, typed by @neosocial/protocol, with the access
 * token attached, one shared refresh on 401, and errors turned into
 * `ApiRequestError` (the server's `{ code, message, errors? }`) or
 * `NetworkError` (no answer at all). It never retries a request that
 * changed something, except once after a successful token refresh.
 */
import {
  ApiErrorCode,
  isApiError,
  Routes,
  type ApiError,
  type AuthTokens,
  type RefreshResponse,
} from '@neosocial/protocol';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: number;
  readonly body: ApiError | null;
  /** Seconds, from a 429. */
  readonly retryAfter: number | null;
  constructor(status: number, body: ApiError | null, retryAfter: number | null = null) {
    super(body?.message ?? `HTTP ${status}`);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body?.code ?? (status === 429 ? ApiErrorCode.RATE_LIMITED : ApiErrorCode.GENERAL);
    this.body = body;
    this.retryAfter = retryAfter;
  }
  /** Field errors by dotted path, first code only. */
  fieldCodes(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(this.body?.errors ?? {})) if (v[0]) out[k] = v[0].code;
    return out;
  }
}

/** The server could not be reached, or answered with something that is not our API. */
export class NetworkError extends Error {
  constructor(message = 'network error', options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'NetworkError';
  }
}

export function isApiRequestError(e: unknown, code?: number): e is ApiRequestError {
  return e instanceof ApiRequestError && (code === undefined || e.code === code);
}

/** Where tokens live. The access token stays in memory; the refresh token persists. */
export interface TokenStore {
  access(): string | null;
  /** Unix ms when the access token expires, or null. */
  accessExpiresAt(): number | null;
  refresh(): string | null;
  save(tokens: AuthTokens): void;
  clear(): void;
}

export interface ApiClientOptions {
  /** Full base, for example "https://host/api/v1" or "/api/v1". */
  baseUrl: string | (() => string);
  tokens: TokenStore;
  fetch?: typeof fetch;
  /** Called when the refresh token is refused: the session is over. */
  onSessionExpired?: () => void;
  /** Called after every successful refresh. */
  onTokens?: (tokens: AuthTokens) => void;
  /** Called after every request: the server answered (any HTTP status) or not at all. */
  onReach?: (answered: boolean) => void;
  now?: () => number;
  /**
   * Runs `fn` holding the named lock across every tab and window of this
   * origin. Default: the Web Locks API (`navigator.locks`), or no lock where
   * it is missing (the server's short reuse grace covers that case).
   */
  lock?: <T>(name: string, fn: () => Promise<T>) => Promise<T>;
}

/** The cross-tab lock every refresh holds (review finding 4). */
export const REFRESH_LOCK = 'ns-refresh';

interface WebLocks {
  request<T>(name: string, cb: () => Promise<T>): Promise<T>;
}

function webLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const locks = (globalThis.navigator as { locks?: WebLocks } | undefined)?.locks;
  return locks ? locks.request(name, fn) : fn();
}

export interface RequestOptions {
  body?: unknown;
  form?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Send the access token (default true). */
  auth?: boolean;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Audit log reason (moderation). */
  reason?: string;
  /**
   * Upload progress, 0..1. fetch cannot report it, so a request with this
   * set goes through XMLHttpRequest (where the browser has it).
   */
  onUploadProgress?: (fraction: number) => void;
}

/** fetch's shape over XMLHttpRequest, for upload progress. */
function xhrFetch(
  url: string,
  init: { method: string; headers: Record<string, string>; body?: BodyInit; signal?: AbortSignal },
  onProgress: (fraction: number) => void,
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(init.method, url);
    for (const [k, v] of Object.entries(init.headers)) xhr.setRequestHeader(k, v);
    xhr.responseType = 'arraybuffer';
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      const headers = new Headers();
      for (const line of xhr
        .getAllResponseHeaders()
        .trim()
        .split(/[\r\n]+/)) {
        const i = line.indexOf(':');
        if (i > 0) headers.append(line.slice(0, i).trim(), line.slice(i + 1).trim());
      }
      const empty = xhr.status === 204 || xhr.status === 205 || xhr.status === 304;
      resolve(
        new Response(empty ? null : (xhr.response as ArrayBuffer), {
          status: xhr.status,
          statusText: xhr.statusText,
          headers,
        }),
      );
    };
    xhr.onerror = () => reject(new TypeError('network error'));
    xhr.ontimeout = () => reject(new TypeError('network timeout'));
    const abort = () => xhr.abort();
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    if (init.signal) {
      if (init.signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      init.signal.addEventListener('abort', abort, { once: true });
      xhr.onloadend = () => init.signal?.removeEventListener('abort', abort);
    }
    xhr.send(init.body as XMLHttpRequestBodyInit | null | undefined);
  });
}

/** Refresh this long before the access token runs out. */
const REFRESH_EARLY_MS = 30_000;

export class ApiClient {
  private refreshing: Promise<boolean> | null = null;
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => number;

  constructor(private readonly opts: ApiClientOptions) {
    this.fetchImpl = opts.fetch ?? ((...a) => globalThis.fetch(...a));
    this.now = opts.now ?? Date.now;
  }

  get tokens(): TokenStore {
    return this.opts.tokens;
  }

  private base(): string {
    return typeof this.opts.baseUrl === 'function' ? this.opts.baseUrl() : this.opts.baseUrl;
  }

  url(path: string, query?: RequestOptions['query']): string {
    let u = this.base() + path;
    if (query) {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(query))
        if (v !== undefined && v !== null) q.set(k, String(v));
      const s = q.toString();
      if (s) u += (u.includes('?') ? '&' : '?') + s;
    }
    return u;
  }

  get<T>(path: string, o?: RequestOptions) {
    return this.request<T>('GET', path, o);
  }
  post<T>(path: string, body?: unknown, o?: RequestOptions) {
    return this.request<T>('POST', path, { ...o, body });
  }
  patch<T>(path: string, body?: unknown, o?: RequestOptions) {
    return this.request<T>('PATCH', path, { ...o, body });
  }
  put<T>(path: string, body?: unknown, o?: RequestOptions) {
    return this.request<T>('PUT', path, { ...o, body });
  }
  delete<T>(path: string, o?: RequestOptions) {
    return this.request<T>('DELETE', path, o);
  }

  /** A valid access token, refreshing first if it is about to expire. */
  async accessToken(): Promise<string | null> {
    const t = this.opts.tokens;
    const exp = t.accessExpiresAt();
    if (t.access() && (exp === null || exp - this.now() > REFRESH_EARLY_MS)) return t.access();
    if (!t.refresh()) return t.access();
    const ok = await this.refreshOnce();
    return ok ? t.access() : null;
  }

  /** One refresh at a time; concurrent 401s share it. */
  refreshOnce(): Promise<boolean> {
    if (!this.refreshing) {
      this.refreshing = this.doRefresh().finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }

  /**
   * The refresh token is shared by every tab (localStorage) and works once.
   * Each tab refreshes holding one cross-tab lock and reads the token only
   * once it holds it, so a tab that waited uses the token the previous
   * holder saved instead of presenting the spent one (which the server
   * treats as theft and answers by signing the session out).
   */
  private doRefresh(): Promise<boolean> {
    return (this.opts.lock ?? webLock)(REFRESH_LOCK, () => this.refreshWithStored());
  }

  private async refreshWithStored(): Promise<boolean> {
    const rt = this.opts.tokens.refresh();
    if (!rt) return false;
    let res: Response;
    try {
      res = await this.fetchImpl(this.url(Routes.authRefresh()), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      });
    } catch (e) {
      // offline: keep the session, the caller sees a network error
      this.opts.onReach?.(false);
      throw new NetworkError('refresh failed: server unreachable', { cause: e });
    }
    this.opts.onReach?.(true);
    if (res.ok) {
      const tokens = (await res.json()) as RefreshResponse;
      this.opts.tokens.save(tokens);
      this.opts.onTokens?.(tokens);
      return true;
    }
    if (res.status === 401 || res.status === 400 || res.status === 403) {
      this.opts.tokens.clear();
      this.opts.onSessionExpired?.();
      return false;
    }
    // 5xx or 429: the session may still be fine
    throw await this.toError(res);
  }

  async request<T>(method: string, path: string, o: RequestOptions = {}): Promise<T> {
    const auth = o.auth !== false;
    const send = async (): Promise<Response> => {
      const headers: Record<string, string> = { ...o.headers };
      if (auth) {
        const token = await this.accessToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      if (o.reason) headers['X-Audit-Log-Reason'] = encodeURIComponent(o.reason);
      let body: BodyInit | undefined;
      if (o.form) body = o.form;
      else if (o.body !== undefined) {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(o.body);
      }
      let res: Response;
      try {
        // Upload progress needs XMLHttpRequest; an injected fetch (tests) keeps fetch.
        if (o.onUploadProgress && !this.opts.fetch && typeof XMLHttpRequest !== 'undefined')
          res = await xhrFetch(
            this.url(path, o.query),
            { method, headers, body, signal: o.signal },
            o.onUploadProgress,
          );
        else
          res = await this.fetchImpl(this.url(path, o.query), {
            method,
            headers,
            body,
            signal: o.signal,
          });
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') throw e;
        this.opts.onReach?.(false);
        throw new NetworkError(`${method} ${path}: server unreachable`, { cause: e });
      }
      this.opts.onReach?.(true);
      return res;
    };

    let res = await send();
    if (res.status === 401 && auth && this.opts.tokens.refresh()) {
      const ok = await this.refreshOnce();
      if (ok) res = await send();
    }
    if (!res.ok) throw await this.toError(res);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    if (!text) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch (e) {
      throw new NetworkError(`${method} ${path}: not JSON`, { cause: e });
    }
  }

  private async toError(res: Response): Promise<ApiRequestError> {
    let body: ApiError | null = null;
    try {
      const j: unknown = await res.json();
      if (isApiError(j)) body = j;
    } catch {
      /* not JSON */
    }
    let retry: number | null = null;
    if (res.status === 429) {
      const h = Number(res.headers.get('Retry-After'));
      const b = (body as unknown as { retry_after?: number } | null)?.retry_after;
      retry = Number.isFinite(b) && b !== undefined ? b : Number.isFinite(h) ? h : null;
    }
    return new ApiRequestError(res.status, body, retry);
  }
}
